import React, { useEffect, useState } from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useDispatch } from "react-redux";

import { fetchPriceSummary } from "../../actions/priceActions";

import { useCart } from "../../components/CartContext";

import "./css/CheckoutScreen.css";


function CheckoutScreen() {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    restaurantId,
    tableId,
  } = useParams();


  const {
    cart,
    cartCount,
  } = useCart();


  /* =========================================================
     PRICE SUMMARY
  ========================================================= */

  const [priceSummary, setPriceSummary] = useState({
    subtotal: 0,
    tax: 0,
    serviceCharge: 0,
    total: 0,
  });


  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");


  /* =========================================================
     PATH
  ========================================================= */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =========================================================
     FETCH PRICE SUMMARY
     
     Uses COMMON Redux action.
     
     No Axios directly inside this screen.
  ========================================================= */

  useEffect(() => {

    const loadPriceSummary = async () => {

      if (!cart || cart.length === 0) {

        setPriceSummary({
          subtotal: 0,
          tax: 0,
          serviceCharge: 0,
          total: 0,
        });

        setLoading(false);

        return;
      }


      try {

        setLoading(true);

        setError("");


        /* -----------------------------------------------
           Convert CartContext format to API format
        ----------------------------------------------- */

        const items = cart.map((item) => ({
          menu_item_id: item.id,
          quantity: item.quantity,
        }));


        /* -----------------------------------------------
           COMMON REDUX ACTION
        ----------------------------------------------- */

        const data = await dispatch(
          fetchPriceSummary(
            restaurantId,
            items
          )
        );


        /* -----------------------------------------------
           STORE PRICE SUMMARY
        ----------------------------------------------- */

        setPriceSummary({

          subtotal:
            Number(
              data?.subtotal || 0
            ),

          tax:
            Number(
              data?.tax || 0
            ),

          serviceCharge:
            Number(
              data?.service_charge || 0
            ),

          total:
            Number(
              data?.total || 0
            ),

        });

      } catch (err) {

        console.error(
          "Price summary error:",
          err
        );


        setError(
          err.response?.data?.detail ||
          err.message ||
          "Unable to calculate your order total."
        );

      } finally {

        setLoading(false);

      }

    };


    loadPriceSummary();

  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  /* =========================================================
     EMPTY CART
  ========================================================= */

  if (!cart || cart.length === 0) {

    return (

      <div className="t6-checkout-page">

        <div className="t6-checkout-empty">

          <div className="t6-checkout-empty-icon">
            🛒
          </div>


          <span className="t6-checkout-eyebrow">
            CHECKOUT
          </span>


          <h1>
            Your order is empty
          </h1>


          <p>
            Add something delicious from our
            Mediterranean menu before continuing.
          </p>


          <button
            type="button"
            className="t6-checkout-primary-button"
            onClick={() => navigate(homePath)}
          >
            Back to menu

            <span>
              →
            </span>

          </button>

        </div>

      </div>

    );
  }


  /* =========================================================
     BACK TO CART
  ========================================================= */

  const handleBack = () => {

    navigate(
      `${homePath}/cart`
    );

  };


  /* =========================================================
     ADD MORE ITEMS
  ========================================================= */

  const handleAddMore = () => {

    navigate(homePath);

  };


  /* =========================================================
     CONTINUE TO ORDER
  ========================================================= */

  const handleContinue = () => {

    if (loading) {
      return;
    }


    if (error) {
      return;
    }


    navigate(
      `${homePath}/order`,
      {
        state: {

          subtotal:
            priceSummary.subtotal,

          tax:
            priceSummary.tax,

          serviceCharge:
            priceSummary.serviceCharge,

          total:
            priceSummary.total,

        },
      }
    );

  };


  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <div className="t6-checkout-page">


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="t6-checkout-hero">

        <div className="t6-checkout-hero-inner">


          <button
            type="button"
            className="t6-checkout-back"
            onClick={handleBack}
          >
            ← Back to order
          </button>


          <span className="t6-checkout-eyebrow">
            CHECKOUT
          </span>


          <h1>
            Almost there
          </h1>


          <p>
            Review your order before choosing
            how you'd like to pay.
          </p>

        </div>

      </section>


      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <main className="t6-checkout-container">

        <div className="t6-checkout-layout">


          {/* =================================================
              LEFT - ORDER ITEMS
          ================================================= */}

          <section className="t6-checkout-items-section">


            {/* SECTION HEADER */}

            <div className="t6-checkout-section-heading">

              <div>

                <span>
                  YOUR ORDER
                </span>

                <h2>
                  Table {tableId}
                </h2>

              </div>


              <strong>

                {cartCount}

                {" "}

                {cartCount === 1
                  ? "item"
                  : "items"}

              </strong>

            </div>


            {/* =================================================
                ITEMS
            ================================================= */}

            <div className="t6-checkout-items">

              {cart.map((item) => (

                <article
                  className="t6-checkout-item"
                  key={item.id}
                >


                  {/* IMAGE */}

                  <div className="t6-checkout-item-image">

                    {item.image ? (

                      <img
                        src={item.image}
                        alt={item.name}
                      />

                    ) : (

                      <div className="t6-checkout-image-placeholder">
                        K
                      </div>

                    )}

                  </div>


                  {/* ITEM INFORMATION */}

                  <div className="t6-checkout-item-info">

                    <h3>
                      {item.name}
                    </h3>


                    <span>

                      ₹
                      {Number(
                        item.price
                      ).toFixed(2)}

                      {" × "}

                      {item.quantity}

                    </span>

                  </div>


                  {/* ITEM TOTAL */}

                  <strong className="t6-checkout-item-total">

                    ₹
                    {(
                      Number(item.price) *
                      Number(item.quantity)
                    ).toFixed(2)}

                  </strong>

                </article>

              ))}

            </div>


            {/* =================================================
                ADD MORE ITEMS
            ================================================= */}

            <button
              type="button"
              className="t6-checkout-add-more"
              onClick={handleAddMore}
            >
              + Add more items
            </button>

          </section>


          {/* =================================================
              RIGHT - SUMMARY
          ================================================= */}

          <aside className="t6-checkout-summary">


            <span className="t6-summary-eyebrow">
              ORDER SUMMARY
            </span>


            <h2>
              Your total
            </h2>


            {/* =================================================
                SUBTOTAL
            ================================================= */}

            <div className="t6-checkout-summary-line">

              <span>
                Subtotal
              </span>

              <strong>

                ₹
                {Number(
                  priceSummary.subtotal
                ).toFixed(2)}

              </strong>

            </div>


            {/* =================================================
                TAX
            ================================================= */}

            <div className="t6-checkout-summary-line">

              <span>
                Tax
              </span>

              <strong>

                ₹
                {Number(
                  priceSummary.tax
                ).toFixed(2)}

              </strong>

            </div>


            {/* =================================================
                SERVICE CHARGE
            ================================================= */}

            <div className="t6-checkout-summary-line">

              <span>
                Service charge
              </span>

              <strong>

                ₹
                {Number(
                  priceSummary.serviceCharge
                ).toFixed(2)}

              </strong>

            </div>


            {/* DIVIDER */}

            <div className="t6-checkout-divider" />


            {/* =================================================
                TOTAL
            ================================================= */}

            <div className="t6-checkout-total">

              <span>
                Total
              </span>

              <strong>

                ₹
                {Number(
                  priceSummary.total
                ).toFixed(2)}

              </strong>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

              <div className="t6-checkout-error">

                {error}

              </div>

            )}


            {/* =================================================
                CONTINUE
            ================================================= */}

            <button
              type="button"
              className="t6-checkout-continue"
              onClick={handleContinue}
              disabled={loading || !!error}
            >

              {loading
                ? "Calculating..."
                : "Continue to order"}


              {!loading && !error && (

                <span>
                  →
                </span>

              )}

            </button>


            {/* =================================================
                NOTE
            ================================================= */}

            <p className="t6-checkout-note">

              Payment method and order details
              will be selected on the next step.

            </p>

          </aside>

        </div>

      </main>

    </div>

  );
}


export default CheckoutScreen;